import { Image, Period, Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { FormattedAdditionalFee } from '@wix/pricing-plans-tpa-api';
import { isFreeVariant, isRecurringVariant, isUnlimitedVariant } from '@wix/pricing-plans-utils';
import { CoverImage } from '@wix/pricing-plans-utils/client-data';
import { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import { NO_BREAK_SPACE } from '../constants';
import { getFormattedPriceData } from '../hooks';

export function planImageToCoverImage(image: Image | undefined): CoverImage | undefined {
  const { id, width, height, altText } = image ?? {};
  if (!id || !width || !height) {
    return undefined;
  }
  return { uri: id, width, height, altText: altText ?? undefined };
}

export function getPlanBenefits(plan: Plan): string[] {
  return plan.perks?.map((p) => p.description ?? '') ?? [];
}

export function hasVariantFreeTrialDays(variant: PricingVariant): boolean {
  return !!variant.freeTrialDays;
}

export function getVariantFreeTrialDaysLabel(variant: PricingVariant, t: TFunction) {
  if (!variant.freeTrialDays) {
    return '';
  }

  if (isUnlimitedVariant(variant)) {
    return t('pp.free-trial-days-unlimited', { count: variant.freeTrialDays });
  }

  return t('pp.free-trial-days', { count: variant.freeTrialDays });
}

export function getVariantAdditionalFees(params: {
  variant: PricingVariant;
  currency: string | undefined;
  locale?: string;
  createCurrencyFormatter: CreateCurrencyFormatter;
  hideTrailingFractionZeroes?: boolean;
}): FormattedAdditionalFee[] {
  const { variant, currency, locale, createCurrencyFormatter, hideTrailingFractionZeroes } = params;
  if (!variant.fees?.length) {
    return [];
  }

  return variant.fees
    .filter((fee) => Boolean(fee.fixedAmountOptions?.amount))
    .map((fee) => {
      const formatted = getFormattedPriceData({
        createCurrencyFormatter,
        locale,
        value: fee.fixedAmountOptions?.amount,
        currency,
        hideTrailingFractionZeroes,
      });

      return { name: fee.name!, amount: `${formatted.fullPrice}` };
    });
}

export function getVariantValidityCycle(variant: PricingVariant, t: TFunction) {
  if (isUnlimitedVariant(variant)) {
    if (isFreeVariant(variant)) {
      return t('pp.plan-validity.free-plan');
    }
    return NO_BREAK_SPACE;
  } else {
    return getVariantDurationWithoutUnlimited(variant, t);
  }
}

export function getVariantDuration(variant: PricingVariant, t: TFunction) {
  if (isUnlimitedVariant(variant)) {
    return t('pp.plan-validity.valid-unlimited');
  } else {
    return getVariantDurationWithoutUnlimited(variant, t);
  }
}

export function getVariantPeriodLabel(variant: PricingVariant, t: TFunction) {
  if (!isRecurringVariant(variant)) {
    return null;
  }

  const unit = variant.billingTerms?.billingCycle?.period;
  const cycleDuration = parseInt(variant.billingTerms?.billingCycle?.count ?? '', 10);

  switch (unit) {
    case Period.DAY:
      return t('pp.period-recurrency.every-day-plural', { count: cycleDuration });
    case Period.WEEK:
      return t('pp.period-recurrency.every-week-plural', { count: cycleDuration });
    case Period.YEAR:
      return t('pp.period-recurrency.every-year-plural', { count: cycleDuration });
    case Period.MONTH:
      return t('pp.period-recurrency.every-month-plural', { count: cycleDuration });
    default:
      return '';
  }
}

function getVariantDurationWithoutUnlimited(variant: PricingVariant, t: TFunction) {
  const unit = variant.billingTerms?.billingCycle?.period;
  const singleCycleDuration = parseInt(variant.billingTerms?.billingCycle?.count ?? '', 10);
  const totalCycleCount = parseInt(variant.billingTerms?.cyclesCompletedDetails?.billingCycleCount ?? '', 10);

  if (!unit || !singleCycleDuration || !totalCycleCount) {
    return '';
  }

  const planDuration = totalCycleCount * singleCycleDuration;

  switch (unit) {
    case Period.DAY:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-days', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-day');
    case Period.WEEK:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-weeks', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-week');
    case Period.YEAR:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-years', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-year');
    default:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-months', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-month');
  }
}
